import React, { useState, useEffect } from "react";

const ChevronDownIcon = ({ className = "h-3 w-3" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.22 7.22a.75.75 0 0 1 1.06 0L10 10.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 8.28a.75.75 0 0 1 0-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

const ChevronLeftIcon = ({ className = "h-3 w-3" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M12.78 5.22a.75.75 0 0 1 0 1.06L9.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = ({ className = "h-3 w-3" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowUpRightIcon = ({ className = "h-3 w-3" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

const CheckIcon = ({ className = "w-3 h-3" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M16.704 5.29a1 1 0 0 1 0 1.415l-8 8a1 1 0 0 1-1.415 0l-4-4a1 1 0 1 1 1.415-1.414L8 12.585l7.29-7.295a1 1 0 0 1 1.414 0Z"
      clipRule="evenodd"
    />
  </svg>
);

const Spinner = ({ className = "h-4 w-4 text-[#6B6B66]" }) => (
  <span
    role="status"
    aria-hidden="true"
    className={`inline-block rounded-full border-2 border-current border-t-transparent animate-spin align-[-2px] ${className}`}
  />
);

function PricingBrowser({
  productTypeImages = {},
  proDashboardPath = "/apps/frame/pro",
  customSizesPath = "/apps/frame/pro/custom-sizes",
}) {
  const [currentStep, setCurrentStep] = useState("type-selection");
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [frameSkuData, setFrameSkuData] = useState(null);
  const [frameSkuLoading, setFrameSkuLoading] = useState(false);
  const [frameSkuError, setFrameSkuError] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({
    mat_style: "",
    glass_type: "",
    paper_type: "",
    frame_style_colour: "",
    frame_sku_size: "",
  });
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [loadingProductType, setLoadingProductType] = useState(null);

  const productTypes = [
    {
      id: "matted",
      kicker: "01 — Matted",
      label: "Print, Frame & Mat Border",
      image: productTypeImages.matted,
      endpoint: "matted.json",
    },
    {
      id: "unmatted",
      kicker: "02 — No mat",
      label: "Print, Frame & No Mat",
      image: productTypeImages.unmatted,
      endpoint: "unmatted.json",
    },
    {
      id: "canvas",
      kicker: "03 — Canvas",
      label: "Canvas Print & Frame",
      image: productTypeImages.canvas,
      endpoint: "canvas.json",
    },
    {
      id: "print-only",
      kicker: "04 — Print only",
      label: "Print Only (Unframed)",
      image: productTypeImages.printOnly,
      endpoint: "unframed.json",
    },
  ];

  const getApiUrl = () => {
    if (!window.FramefoxConfig || !window.FramefoxConfig.apiUrl) {
      throw new Error("Pricing API is unavailable");
    }

    return window.FramefoxConfig.apiUrl;
  };

  const fetchFrameSkuData = async (productType) => {
    setFrameSkuLoading(true);
    setFrameSkuError(null);
    setLoadingProductType(productType);

    try {
      const endpoint = productTypes.find(
        (type) => type.id === productType,
      )?.endpoint;
      const baseUrl = getApiUrl();
      const url = `${baseUrl}/frame_skus/${endpoint}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch frame SKU data: ${response.statusText}`,
        );
      }

      const data = await response.json();
      setFrameSkuData(data);
      setCurrentStep("option-selection");
    } catch (err) {
      setFrameSkuError(err.message);
    } finally {
      setFrameSkuLoading(false);
      setLoadingProductType(null);
    }
  };

  const handleProductTypeSelect = (productType) => {
    setSelectedProductType(productType);
    fetchFrameSkuData(productType);
  };

  // Auto-select a product type from ?type=<id> on mount (deep link from
  // dashboard panels). Strips the param from the URL afterwards so that
  // resetting back to the type-selection step doesn't re-trigger.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const requestedType = params.get("type");
    if (!requestedType) return;
    const match = productTypes.find((t) => t.id === requestedType);
    if (!match) return;

    handleProductTypeSelect(match.id);

    params.delete("type");
    const cleaned =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", cleaned);
  }, []);

  const searchFrameSkus = async (options) => {
    setSearchLoading(true);
    setSearchError(null);

    try {
      const params = new URLSearchParams();

      if (options.paper_type)
        params.append("paper_type_id", options.paper_type);
      if (options.glass_type)
        params.append("glass_type_id", options.glass_type);
      if (options.mat_style) params.append("mat_style_id", options.mat_style);
      if (options.frame_style_colour)
        params.append("frame_style_colour_id", options.frame_style_colour);
      if (options.frame_sku_size)
        params.append("frame_sku_size_id", options.frame_sku_size);

      const baseUrl = getApiUrl();
      const url = `${baseUrl}/frame_skus.json${
        params.toString() ? "?" + params.toString() : ""
      }`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to search frame SKUs: ${response.statusText}`);
      }

      const data = await response.json();
      setSearchResults(data.frame_skus || []);
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleOptionChange = (optionType, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionType]: value,
    }));
  };

  useEffect(() => {
    if (!frameSkuData || frameSkuLoading || frameSkuError) return;
    searchFrameSkus(selectedOptions);
  }, [selectedOptions]);

  useEffect(() => {
    if (!frameSkuData || frameSkuLoading || frameSkuError) return;

    const availableColours = frameSkuData.frame_style_colours || [];
    const availableMatStyles = frameSkuData.mat_styles || [];
    const availableGlassTypes = frameSkuData.glass_types || [];
    const availablePaperTypes = frameSkuData.paper_types || [];

    if (availableColours.length > 0) {
      const collections = [
        ...new Set(availableColours.map((c) => c.collection).filter(Boolean)),
      ];
      if (collections.length > 0 && !collections.includes(selectedCollection)) {
        setSelectedCollection(collections[0]);
      }
    }

    const autoSelectedOptions = {};
    if (availableMatStyles.length > 0)
      autoSelectedOptions.mat_style = availableMatStyles[0].id;
    if (availableGlassTypes.length > 0)
      autoSelectedOptions.glass_type = availableGlassTypes[0].id;
    if (availablePaperTypes.length > 0)
      autoSelectedOptions.paper_type = availablePaperTypes[0].id;
    if (availableColours.length > 0)
      autoSelectedOptions.frame_style_colour = availableColours[0].id;

    setSelectedOptions((prev) => ({
      ...prev,
      ...autoSelectedOptions,
    }));

    searchFrameSkus(autoSelectedOptions);
  }, [frameSkuData, frameSkuLoading, frameSkuError]);

  const handleBackToTypeSelection = () => {
    setCurrentStep("type-selection");
    setSelectedProductType(null);
    setFrameSkuData(null);
    setFrameSkuError(null);
    setSearchResults(null);
    setSearchError(null);
    setSelectedCollection(null);
    setSelectedOptions({
      mat_style: "",
      glass_type: "",
      paper_type: "",
      frame_style_colour: "",
      frame_sku_size: "",
    });
  };

  const formatCentsToPrice = (cents) => {
    if (!cents && cents !== 0) return "N/A";
    return `$${(cents / 100).toFixed(2)}`;
  };

  const selectClasses =
    "appearance-none bg-none w-full h-9 rounded-md border border-[#E4E0D6] bg-white pl-3 pr-8 text-[12px] text-[#111111] focus:outline-none focus:ring-1 focus:ring-[#111111] focus:border-[#111111] cursor-pointer";

  const ChevronAdornment = () => (
    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
      <ChevronDownIcon className="h-3 w-3 text-[#6B6B66]" />
    </span>
  );

  // ---------- Type-selection step ----------
  if (currentStep === "type-selection") {
    return (
      <div className="flex flex-col gap-6">
        {/* Page header — only on type-selection */}
        <div className="flex flex-col gap-2">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-[12px] text-[#6B6B66]"
          >
            <a
              href={proDashboardPath}
              className="hover:text-[#111111] no-underline text-[#6B6B66]"
            >
              Pro Dashboard
            </a>
            <span className="text-[#9A9890]">/</span>
            <span className="text-[#111111]">Pricing</span>
          </nav>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-baseline gap-2 min-w-0">
              <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#111111] truncate m-0">
                Pricing
              </h1>
              <span className="inline-flex items-center h-4 rounded-lg px-1.5 bg-[#F6F4EE] shrink-0">
                <span className="font-mono text-[10px] font-medium text-[#6B6B66] whitespace-nowrap">
                  4 product lines
                </span>
              </span>
            </div>
          </div>
          <p className="text-[12px] text-[#6B6B66] m-0 max-w-2xl">
            Browse Pro pricing across our four core product lines.
          </p>
        </div>

        {/* Product-type chooser */}
        <div className="rounded-lg border border-[#E4E0D6] bg-white overflow-hidden">
          <div className="flex items-center h-9 px-6 lg:px-8 gap-2 bg-[#F6F4EE] border-b border-[#E4E0D6]">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
              Choose a product line
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {productTypes.map((product, index) => {
              const isLoading = loadingProductType === product.id;
              const borderClasses = [
                index !== productTypes.length - 1 ? "lg:border-r" : "",
                index % 2 === 0 ? "sm:border-r lg:border-r" : "",
                index < 2 ? "border-b sm:border-b lg:border-b-0" : "",
                "border-[#E4E0D6]",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleProductTypeSelect(product.id)}
                  disabled={loadingProductType !== null}
                  className={`group relative flex flex-col bg-white hover:bg-[#FAF8F2] transition-colors text-left p-0 cursor-pointer disabled:cursor-not-allowed ${borderClasses}`}
                >
                  <div className="bg-[#F6F4EE] aspect-square overflow-hidden border-b border-[#E4E0D6]">
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.label}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                      />
                    )}
                  </div>
                  <div className="flex flex-col py-5 px-5 gap-1.5 flex-1">
                    <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                      {product.kicker}
                    </span>
                    <span className="text-[14px] font-semibold tracking-[-0.01em] text-[#111111]">
                      {product.label}
                    </span>
                    <div className="flex items-center gap-1 mt-2 text-[11px] font-medium text-[#B5651D] group-hover:text-[#111111]">
                      <span>View pricing</span>
                      <ArrowRightIcon className="h-3 w-3" />
                    </div>
                  </div>

                  {isLoading && (
                    <div className="absolute inset-0 bg-white/85 flex items-center justify-center">
                      <Spinner className="h-6 w-6 text-[#111111]" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Option-selection step ----------
  const productTypeMeta = productTypes.find(
    (type) => type.id === selectedProductType,
  );
  const productTypeLabel = productTypeMeta?.label || "";

  return (
    <div className="flex flex-col gap-6">
      {/* Sub-header for the option-selection step */}
      <div className="flex flex-col gap-2">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-[12px] text-[#6B6B66]"
        >
          <a
            href={proDashboardPath}
            className="hover:text-[#111111] no-underline text-[#6B6B66]"
          >
            Pro Dashboard
          </a>
          <span className="text-[#9A9890]">/</span>
          <button
            type="button"
            onClick={handleBackToTypeSelection}
            className="hover:text-[#111111] bg-transparent border-0 p-0 cursor-pointer text-[#6B6B66] text-[12px]"
          >
            Pricing
          </button>
          <span className="text-[#9A9890]">/</span>
          <span className="text-[#111111]">{productTypeLabel}</span>
        </nav>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-2xl font-mono font-medium text-[#9A9890]">
              {productTypeMeta?.kicker?.split(" — ")[0] || "PRC"}
            </span>
            <h2 className="text-2xl font-bold tracking-[-0.01em] text-[#111111] truncate m-0">
              {productTypeLabel}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleBackToTypeSelection}
            className="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 border border-[#E4E0D6] bg-white text-[11px] font-medium text-[#111111] hover:bg-[#FAF8F2] cursor-pointer transition-colors"
          >
            <ChevronLeftIcon className="h-3 w-3" />
            Back to product selection
          </button>
        </div>
      </div>

      {/* Loading the product-type metadata */}
      {frameSkuLoading && (
        <div className="rounded-lg border border-[#E4E0D6] bg-white py-12 flex items-center justify-center gap-2">
          <Spinner className="h-4 w-4 text-[#6B6B66]" />
          <span className="text-[12px] text-[#6B6B66]">Loading options…</span>
        </div>
      )}

      {/* Error loading the product-type metadata */}
      {frameSkuError && !frameSkuLoading && (
        <div className="rounded-lg border border-[#EADFBE] bg-[#FBF1E5] px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <span className="text-[12px] text-[#7A5A0F]">{frameSkuError}</span>
          <button
            type="button"
            onClick={() => fetchFrameSkuData(selectedProductType)}
            className="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 bg-[#111111] text-white text-[11px] font-medium hover:opacity-90 cursor-pointer transition-opacity"
          >
            Try again
          </button>
        </div>
      )}

      {/* Options card */}
      {frameSkuData && !frameSkuLoading && !frameSkuError && (
        <div className="rounded-lg border border-[#E4E0D6] bg-white overflow-hidden">
          <div className="flex items-center h-9 px-6 lg:px-8 gap-2 bg-[#F6F4EE] border-b border-[#E4E0D6]">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
              Step 02
            </span>
            <span className="text-[10px] uppercase tracking-[0.06em] text-[#111111]">
              Choose your specifications
            </span>
          </div>

          <div className="px-6 lg:px-8 py-5 flex flex-col gap-5">
            {/* Frame style colours */}
            {frameSkuData.frame_style_colours &&
              frameSkuData.frame_style_colours.length > 0 && (
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                    Frame style
                  </label>

                  {(() => {
                    const collections = [
                      ...new Set(
                        frameSkuData.frame_style_colours
                          .map((c) => c.collection)
                          .filter(Boolean),
                      ),
                    ];
                    return collections.length > 1 ? (
                      <div className="flex gap-1 flex-wrap">
                        {collections.map((collection) => (
                          <button
                            key={collection}
                            type="button"
                            onClick={() => setSelectedCollection(collection)}
                            className={`inline-flex items-center h-7 rounded-md px-2.5 text-[11px] font-medium cursor-pointer transition-colors ${
                              selectedCollection === collection
                                ? "bg-[#111111] text-white"
                                : "border border-[#E4E0D6] bg-white text-[#111111] hover:bg-[#FAF8F2]"
                            }`}
                          >
                            {collection}
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}

                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {frameSkuData.frame_style_colours
                      .filter(
                        (colour) =>
                          !selectedCollection ||
                          colour.collection === selectedCollection,
                      )
                      .map((colour) => {
                        const selected =
                          selectedOptions.frame_style_colour === colour.id;
                        return (
                          <button
                            key={colour.id}
                            type="button"
                            onClick={() =>
                              handleOptionChange(
                                "frame_style_colour",
                                colour.id,
                              )
                            }
                            title={colour.title}
                            className={`shrink-0 relative rounded-md overflow-hidden transition-all cursor-pointer border ${
                              selected
                                ? "border-[#111111] ring-1 ring-[#111111]"
                                : "border-[#E4E0D6] hover:border-[#9A9890]"
                            }`}
                          >
                            <div className="w-12 h-12 bg-[#F6F4EE] block">
                              {colour.profile && (
                                <img
                                  src={colour.profile}
                                  alt={colour.title}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            {selected && (
                              <div className="absolute top-0.5 right-0.5 bg-[#111111] text-white rounded-full p-0.5">
                                <CheckIcon className="w-2.5 h-2.5" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                  </div>

                  {selectedOptions.frame_style_colour && (
                    <div className="text-[11px] text-[#6B6B66]">
                      {(() => {
                        const frameStyle =
                          frameSkuData.frame_style_colours.find(
                            (c) => c.id === selectedOptions.frame_style_colour,
                          );
                        if (!frameStyle) return null;
                        return (
                          <>
                            <span className="text-[#111111] font-medium">
                              {frameStyle.title}
                            </span>
                            {(frameStyle.width || frameStyle.depth) && (
                              <span className="font-mono text-[#9A9890] ml-1.5">
                                Width {frameStyle.width}mm · Depth{" "}
                                {frameStyle.depth}mm
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

            {/* Other selects */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {selectedProductType !== "print-only" &&
                selectedProductType !== "canvas" &&
                frameSkuData.mat_styles &&
                frameSkuData.mat_styles.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                      Mat border
                    </label>
                    <div className="relative">
                      <select
                        value={selectedOptions.mat_style}
                        onChange={(e) =>
                          handleOptionChange("mat_style", e.target.value)
                        }
                        className={selectClasses}
                      >
                        <option value="">All mat styles…</option>
                        {frameSkuData.mat_styles.map((style) => (
                          <option key={style.id} value={style.id}>
                            {style.title}
                          </option>
                        ))}
                      </select>
                      <ChevronAdornment />
                    </div>
                  </div>
                )}

              {selectedProductType !== "print-only" &&
                selectedProductType !== "canvas" &&
                frameSkuData.glass_types &&
                frameSkuData.glass_types.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                      Glass type
                    </label>
                    <div className="relative">
                      <select
                        value={selectedOptions.glass_type}
                        onChange={(e) =>
                          handleOptionChange("glass_type", e.target.value)
                        }
                        className={selectClasses}
                      >
                        <option value="">All glass types…</option>
                        {frameSkuData.glass_types.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.title}
                          </option>
                        ))}
                      </select>
                      <ChevronAdornment />
                    </div>
                  </div>
                )}

              {frameSkuData.paper_types &&
                frameSkuData.paper_types.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                      Paper type
                    </label>
                    <div className="relative">
                      <select
                        value={selectedOptions.paper_type}
                        onChange={(e) =>
                          handleOptionChange("paper_type", e.target.value)
                        }
                        className={selectClasses}
                      >
                        <option value="">All paper types…</option>
                        {frameSkuData.paper_types.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.title}
                          </option>
                        ))}
                      </select>
                      <ChevronAdornment />
                    </div>
                  </div>
                )}

              {frameSkuData.frame_sku_sizes &&
                frameSkuData.frame_sku_sizes.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
                        Print size
                      </label>
                      <a
                        href={customSizesPath}
                        className="text-[11px] font-medium text-[#B5651D] hover:underline"
                      >
                        Custom Print Size Checker
                      </a>
                    </div>
                    <div className="relative">
                      <select
                        value={selectedOptions.frame_sku_size}
                        onChange={(e) =>
                          handleOptionChange("frame_sku_size", e.target.value)
                        }
                        className={selectClasses}
                      >
                        <option value="">All sizes…</option>
                        {frameSkuData.frame_sku_sizes.map((size) => (
                          <option key={size.id} value={size.id}>
                            {size.title}
                          </option>
                        ))}
                      </select>
                      <ChevronAdornment />
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Results card */}
      {(searchLoading || searchError || searchResults) && (
        <div className="rounded-lg border border-[#E4E0D6] bg-white overflow-hidden">
          <div className="flex items-center h-9 px-6 lg:px-8 gap-2 bg-[#F6F4EE] border-b border-[#E4E0D6]">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">
              Step 03
            </span>
            <span className="text-[10px] uppercase tracking-[0.06em] text-[#111111]">
              Matching products
            </span>
            {searchResults && !searchLoading && !searchError && (
              <span className="ml-auto inline-flex items-center h-4 rounded-lg px-1.5 bg-white border border-[#E4E0D6]">
                <span className="font-mono text-[10px] font-medium text-[#6B6B66] whitespace-nowrap">
                  {searchResults.length} result
                  {searchResults.length === 1 ? "" : "s"}
                </span>
              </span>
            )}
          </div>

          {searchLoading && (
            <div className="px-6 py-12 flex items-center justify-center gap-2">
              <Spinner className="h-4 w-4 text-[#6B6B66]" />
              <span className="text-[12px] text-[#6B6B66]">
                Searching frame SKUs…
              </span>
            </div>
          )}

          {searchError && !searchLoading && (
            <div className="px-6 py-6 flex items-center justify-between gap-4 flex-wrap">
              <span className="text-[12px] text-[#7A1F1F]">{searchError}</span>
              <button
                type="button"
                onClick={() => searchFrameSkus(selectedOptions)}
                className="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 bg-[#111111] text-white text-[11px] font-medium hover:opacity-90 cursor-pointer transition-opacity"
              >
                Try again
              </button>
            </div>
          )}

          {searchResults && !searchLoading && !searchError && (
            <>
              {searchResults.length === 0 ? (
                <div className="px-6 py-10 flex items-center justify-center">
                  <div className="border-2 border-dashed border-[#E4E0D6] bg-[#FAF8F2] rounded-md py-8 px-6 max-w-md text-center">
                    <h3 className="text-[13px] font-semibold text-[#111111] m-0 mb-1">
                      No frames found
                    </h3>
                    <p className="text-[12px] text-[#6B6B66] m-0 mb-3">
                      No frame products match the selected options. Try
                      adjusting your filters or selecting a different size.
                    </p>
                    <p className="text-[11px] text-[#7A5A0F] m-0">
                      Frame strength rules:{" "}
                      <span className="font-medium">Skinny</span> styles up to
                      A2, <span className="font-medium">Slim</span> styles up to
                      A1, <span className="font-medium">Wide</span> styles above
                      A1.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-white border-b border-[#E4E0D6]">
                        <th className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Preview
                        </th>
                        <th className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Print size
                        </th>
                        <th className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Frame size
                        </th>
                        <th className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Frame style
                        </th>
                        <th className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Mat
                        </th>
                        <th className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Glass
                        </th>
                        <th className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Paper
                        </th>
                        <th className="text-right text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          <span className="inline-flex items-baseline gap-1.5 justify-end">
                            <span>Price</span>
                            <span className="normal-case tracking-normal text-[9px] font-normal text-[#999]">
                              (inc. GST)
                            </span>
                          </span>
                        </th>
                        <th className="text-right text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890] px-4 py-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E4E0D6]">
                      {searchResults.map((sku) => (
                        <tr key={sku.id} className="hover:bg-[#FAF8F2]">
                          <td className="px-4 py-2 whitespace-nowrap">
                            {sku.preview_image ? (
                              <div className="h-12 w-12 flex items-center justify-center">
                                <img
                                  src={sku.preview_image}
                                  alt={sku.title}
                                  className="max-h-12 max-w-12 object-contain"
                                />
                              </div>
                            ) : (
                              <div className="h-12 w-12 bg-[#F6F4EE] rounded-sm" />
                            )}
                          </td>
                          <td className="px-4 py-2 text-[12px] text-[#111111] whitespace-nowrap">
                            {sku.title || "—"}
                          </td>
                          <td className="px-4 py-2 text-[12px] text-[#6B6B66] whitespace-nowrap">
                            {sku.frame_size || "—"}
                          </td>
                          <td className="px-4 py-2 text-[12px] text-[#6B6B66] whitespace-nowrap">
                            {sku.frame_style || "—"}
                          </td>
                          <td className="px-4 py-2 text-[12px] text-[#6B6B66] whitespace-nowrap">
                            {sku.mat_style || "—"}
                          </td>
                          <td className="px-4 py-2 text-[12px] text-[#6B6B66] whitespace-nowrap">
                            {sku.glass_type || "—"}
                          </td>
                          <td className="px-4 py-2 text-[12px] text-[#6B6B66] whitespace-nowrap">
                            {sku.paper_type || "—"}
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <div className="inline-flex items-baseline gap-1.5 font-mono">
                              {sku.compare_cents > sku.cost_cents && (
                                <span className="text-[11px] font-medium text-[#9A9890] line-through">
                                  {formatCentsToPrice(sku.compare_cents)}
                                </span>
                              )}
                              <span className="text-[12px] font-medium text-[#111111]">
                                {formatCentsToPrice(sku.cost_cents)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {sku.order_url ? (
                              <a
                                href={sku.order_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center h-7 rounded-md px-2.5 gap-1.5 bg-[#111111] text-white text-[11px] font-medium hover:opacity-90 transition-opacity no-underline"
                              >
                                Order product
                                <ArrowUpRightIcon className="h-3 w-3" />
                              </a>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PricingBrowser;
